import React, { FunctionComponent } from 'react';

import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import AppState from '@webapp/store/state';

import { Translate } from 'react-localize-redux';

import { rgba } from 'polished';
import styled, { useTheme } from 'styled-components';

import Box from '@components/layout/box';
import Grid from '@components/basics/grid';
import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import Percentage from '@components/dashboard/metrics-widget/percentage';
import RefIndicator from '@components/dashboard/metrics-widget/ref-indicator';

import { PieChart, Pie, Cell, Tooltip, TooltipProps } from 'recharts';
import {
  ValueType,
  NameType,
} from 'recharts/types/component/DefaultTooltipContent';

import { TooltipWrapper } from './components';

import { numberFormatter } from './utils';

import { useSelector } from 'react-redux';
import { TopDspSummary } from '@services/analytics/types/top-dsp-summary';
import { GetArtistAnalytics } from '@webapp/store/artist-analytics/action-creators';
import { AnalyticsQueryTypes } from '@services/analytics/types/analytics-query-enum';

interface DSPsBoxProps {
  className?: string;
  isMobile: boolean;
  topDsps: TopDspSummary[];
  artistId?: string;
  getTopDspsAnalytics: () => GetArtistAnalytics;
}

const CustomTooltip = ({
  active,
  payload,
}: TooltipProps<ValueType, NameType>) => {
  const [days, range] = useSelector((state: AppState) => {
    return [
      state.artistAnalytics.filters?.days as string,
      state.artistAnalytics.filters?.range as string,
    ];
  });
  if (active && payload && payload.length) {
    return (
      <TooltipWrapper gap={20}>
        {payload.map((item, i) => {
          return (
            <Grid key={i} gap={10}>
              <Typography weight="700">{item.payload.name}</Typography>
              <Flex alignItems="baseline" gap={4}>
                <Typography type="small" weight="700" capitalize>
                  <Translate id="ANALYTICS.STREAMS" />
                </Typography>
                <Typography type="small">
                  {numberFormatter.format(Number(item.payload.streams))}
                </Typography>
              </Flex>
              <Flex alignItems="baseline" gap={4}>
                <Typography type="small" weight="700" capitalize>
                  <Translate id="ANALYTICS.LISTENERS" />
                </Typography>
                <Typography type="small">
                  {numberFormatter.format(Number(item.payload.listeners))}
                </Typography>
              </Flex>
            </Grid>
          );
        })}
        <Typography type="small" isGrey>
          {/* TODO: DEVTEAM-405 - This value should came from what the user selected on the fitlers */}
          {range === 'allTime' ? (
            <Translate id="ANALYTICS.TIMEFRAME.ALL_TIME" />
          ) : (
            <Translate
              id="ANALYTICS.TIMEFRAME.LAST_30_DAYS"
              data={{ days: days }}
            />
          )}
        </Typography>
      </TooltipWrapper>
    );
  }

  return null;
};

const mapStateToProps = (state: AppState) => {
  return {
    isMobile: state.ui.mobile,
    artistId: state.artist.active?.id,
    topDsps: state.artistAnalytics.data?.topDsps || [],
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getTopDspsAnalytics: () =>
    dispatch(new GetArtistAnalytics(AnalyticsQueryTypes.DSPS, undefined, true)),
});

const DSPsBox: FunctionComponent<DSPsBoxProps> = ({
  className,
  isMobile,
  topDsps,
  artistId,
  getTopDspsAnalytics,
}) => {
  const theme = useTheme();

  React.useEffect(() => {
    if (artistId) {
      getTopDspsAnalytics();
    }
  }, [artistId, getTopDspsAnalytics]);

  const getColors = (index: number) => {
    switch (index) {
      case 0:
        return theme.colors.blueColor;
      case 1:
        return rgba(theme.colors.blueColor, 0.5);
      case 2:
        return rgba(theme.colors.blueColor, 0.25);
      case 3:
        return theme.colors.tertiary;

      default:
        return theme.colors.blueColor;
    }
  };

  const dSPsTemplateData: any[] = [];

  const totalStreams =
    (topDsps.reduce((memo, item) => {
      return memo + item.streams;
    }, 0) || 0) / 100;

  let otherStreams = 0;
  let otherListeners = 0;
  let otherChanges = 0;

  topDsps.forEach((item, i) => {
    if (i < 3) {
      dSPsTemplateData.push({
        id: item.dsp.toLowerCase(),
        name: item.dsp,
        streams: item.streams,
        listeners: item.listeners,
        status: item.streamsChange,
        value: totalStreams === 0 ? 0 : item.streams / totalStreams,
        isOther: false,
      });
    } else {
      otherStreams += item.streams;
      otherListeners += item.listeners;
      otherChanges += item.streamsChange;
    }
  });

  if (topDsps.length > 3) {
    dSPsTemplateData.push({
      id: 'other',
      name: <Translate id="ANALYTICS.ENGAGEMENT.DSPS.ITEM.OTHER" />,
      value: totalStreams === 0 ? 0 : otherStreams / totalStreams,
      streams: otherStreams,
      listeners: otherListeners,
      status: otherChanges,
      isOther: true,
    });
  }

  return (
    <Box className={className || ''} noBorderTop noBorderLeft={!isMobile}>
      <Flex justifyContent="space-between" alignItems="center">
        <div>
          <Typography className="title" type="title" customFont="semi-bold">
            <Translate id="ANALYTICS.ENGAGEMENT.DSPS.TITLE" />
          </Typography>
          <Grid gap={isMobile ? 5 : 2}>
            {dSPsTemplateData
              .sort((a, b) => b.streams - a.streams)
              .map((dsp, i) =>
                !isMobile ? (
                  <React.Fragment key={i}>
                    {dsp.streams > 0 && (
                      <Flex key={i} alignItems="center" gap={10}>
                        <RefIndicator customColor={getColors(i)} />
                        <Flex alignItems="baseline" gap={4}>
                          <Typography type="small">{dsp.name}</Typography>
                          <Typography type="small" isGrey>
                            ({dsp.value.toFixed()}%)
                          </Typography>
                          <Percentage
                            value={dsp.status}
                            valueFormat="percentage"
                            noBackground
                          />
                        </Flex>
                      </Flex>
                    )}
                  </React.Fragment>
                ) : (
                  <Grid gap={2} key={i}>
                    <Flex
                      justifyContent="flex-start"
                      alignItems="center"
                      gap={10}
                    >
                      <RefIndicator customColor={getColors(i)} />
                      <Typography type="small">{dsp.name}</Typography>
                    </Flex>
                    <Flex
                      justifyContent="flex-start"
                      alignItems="center"
                      gap={5}
                      style={{ marginLeft: '15px' }}
                    >
                      <Typography type="small" isGrey>
                        ({dsp.value.toFixed()}%)
                      </Typography>
                      <Percentage
                        value={dsp.status}
                        valueFormat="percentage"
                        noBackground
                      />
                    </Flex>
                  </Grid>
                )
              )}
          </Grid>
        </div>
        <div
          style={{
            width: isMobile ? 140 : 120,
            height: isMobile ? 140 : 120,
          }}
        >
          <PieChart width={isMobile ? 140 : 120} height={isMobile ? 140 : 120}>
            <Pie
              dataKey="value"
              data={dSPsTemplateData.map((dsp) => {
                return {
                  ...dsp,
                  value: dsp.streams,
                  streams: dsp.streams,
                };
              })}
              innerRadius="70%"
              outerRadius="100%"
              paddingAngle={4}
              stroke={'transparent'}
            >
              {dSPsTemplateData.map((_entry, index) => (
                <Cell key={index} fill={getColors(index)} />
              ))}
            </Pie>
            <Tooltip
              content={<CustomTooltip />}
              allowEscapeViewBox={!isMobile ? { x: true, y: true } : {}}
            />
          </PieChart>
        </div>
      </Flex>
    </Box>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(styled(DSPsBox)`
  .title {
    margin-bottom: 20px;
  }

  .cover {
    flex-shrink: 0;
  }
`);
